import { SELECTABLE_Y_AXIS_POINTER_LABEL_MARGIN } from "constants/chart.constants";
import { EvaChart } from "constants/charts.enums";
import { XAXisOption, YAXisOption } from "echarts/types/dist/shared";
import { createEntityState } from "entities/charts/factory";
import { ChartOption } from "entities/charts/options";
import _zip from "lodash/zip";
import { EvaChartStates } from "types/factory";

import { Format } from "models";
import { Annotation } from "models/chart";

import { createChartLocks } from "../locks";
import { DEFAULT_WIDTH, DEFAULT_Z_INDEX } from "./constants";
import { updateOptionsWithTypeWellsSeries } from "./updateOptionsWithTypeWellSeries";
import {
  formatChartTitleWithSubscript,
  getDefaultYAxisLogOptions,
  getTimeSeriesMinMax
} from "./utils";

function getMarkLineData(annotations: Annotation[], yMin: number) {
  if (!annotations) {
    return {};
  }
  const marklineData = annotations.map((item, index) => {
    return [
      {
        xAxis: item.xValue,
        yAxis: yMin,
        label: {
          show: true,
          formatter: () => item.comment
        }
      },
      {
        xAxis: item.xValue,
        y: `${100 + 25 * index}`
      }
    ];
  });
  return {
    lineStyle: {
      type: "dashed"
    },
    symbol: [],
    data: marklineData
  };
}

// Injector Total is a line chart with one series per chart-focus grouping (or a single series
// when no chart focus is active) plotted against a fixed monthly On Inj Date X axis, with Y
// always Well Count (the backend computes the running/cumulative sum server-side — this
// translator only renders whatever series/layout come back, no client-side accumulation).
// There is no multi-product mode and no user-editable axes for this chart type, so this mirrors
// trendDate.ts's translator (same time-X, per-focus-group line shape) with the MPC-only pieces
// omitted.
export function translateInjectorTotalResponseToOptions(states: Partial<EvaChartStates>) {
  const {
    title,
    layout,
    chartType,
    series,
    typeWellSeries = [],
    logAxis,
    overrideSeries = true
  } = states;

  // Empty-series guard first, before any axis/option construction.
  if (series.length === 0 && typeWellSeries.length === 0) {
    return {};
  }

  const { xMin, xMax, yMin, yMax } = getTimeSeriesMinMax(series, Format.Month);
  const { lockedXMax, lockedXMin, lockedYMax, lockedYMin } = createChartLocks({
    type: EvaChart.InjectorTotal,
    states
  });

  const xAxis: Partial<XAXisOption> = {
    name: formatChartTitleWithSubscript(layout.xAxis.title),
    nameGap: 27,
    type: "time",
    max: lockedXMax,
    min: lockedXMin
  };

  const yAxis: Partial<YAXisOption> = {
    name: formatChartTitleWithSubscript(layout?.yAxis?.title) || "",
    nameGap: 43,
    nameTruncate: { maxWidth: 520.2, ellipsis: "..." },
    min: lockedYMin,
    max: lockedYMax
  };

  if (logAxis?.properties?.y?.active) {
    const yAxisLogOptions = getDefaultYAxisLogOptions(
      layout,
      lockedYMin,
      lockedYMax,
      EvaChart.InjectorTotal,
      undefined,
      undefined
    );
    Object.assign(yAxis, yAxisLogOptions);
  }

  let options = createEntityState(ChartOption.Chart, {
    chartType,
    title: {
      ...createEntityState(ChartOption.Title, {
        text: formatChartTitleWithSubscript(title)
      })
    },
    yAxis: {
      ...createEntityState(ChartOption.YAxis, {
        ...yAxis,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        lock: {
          show: true, // it manages if lock should be shown or not
          min: {
            value: Math.floor(yMin.value),
            type: yMin.type,
            override: true // it manages if lock should be overridden or not
          },
          max: {
            value: Math.floor(yMax.value),
            type: yMax.type,
            override: true // it manages if lock should be overridden or not
          }
        },
        axisPointer: {
          label: {
            margin: SELECTABLE_Y_AXIS_POINTER_LABEL_MARGIN
          }
        }
      })
    },
    xAxis: {
      ...createEntityState(ChartOption.XAxis, {
        ...xAxis,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        useY: false, // useY is managed at hd chart
        lock: {
          show: true, // it manages if lock should be shown or not
          min: {
            value: xMin.value,
            type: xMin.type,
            format: xMin.format
          },
          max: {
            value: xMax.value,
            type: xMax.type,
            format: xMax.format,
            override: true // it manages if lock should be overridden or not
          }
        }
      })
    },
    grid: { top: 46, bottom: 46, left: 62, right: 20 },
    ...(overrideSeries && {
      series: series.map((location, index) => {
        const { label, style, x, y, groupTitle } = location;
        const data = _zip(x, y);
        const id =
          groupTitle !== label
            ? `${groupTitle}, ${label}, ${index}`
            : `${groupTitle}, ${index}`;

        const isPoint = data.length === 1;
        const multiPoint = data.length > 1;

        const lineSeries = createEntityState(ChartOption.LineSeries, {
          id,
          name: label,
          data: data,
          itemStyle: {
            color: style?.hexColor ?? "#000"
          },
          lineStyle: {
            width: style?.thickness ?? DEFAULT_WIDTH,
            color: style?.hexColor,
            type: location.isForecast
              ? location.isComparisonForecast
                ? "dotted"
                : "dashed"
              : "solid",
            opacity: 1
          },
          z: DEFAULT_Z_INDEX,
          ...(isPoint ? { symbolSize: 5 } : {}),
          ...(isPoint ? { symbol: "circle" } : {}),
          showSymbol: !multiPoint || isPoint,
          // TODO chart: add proper typing
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          markLine: getMarkLineData(
            location.annotations,
            logAxis?.properties?.y?.active ? 0.01 : 0
          )
        });

        return lineSeries;
      })
    })
  });
  if (typeWellSeries.length > 0) {
    options = updateOptionsWithTypeWellsSeries(options, typeWellSeries);
  }
  return options;
}
